import React, { lazy, Suspense } from 'react';
import Main from './components/Main';

import SuperTokens, { SuperTokensWrapper } from 'supertokens-auth-react';
import EmailPassword from 'supertokens-auth-react/recipe/emailpassword';
import Session from 'supertokens-auth-react/recipe/session';
import { APP_CONFIG } from './config/config';
import { ConfirmProvider } from 'lib/confirm';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// Dynamically imported (rather than the previous static `import { ReactQueryDevtools }`)
// so its ~300 kB stays out of the production entry chunk entirely -- it was being
// bundled unconditionally even though it only ever rendered behind `import.meta.env.DEV`.
// Vite statically replaces `import.meta.env.DEV` with `false` in production, so this
// branch -- and the dynamic import() inside it -- never executes at runtime there.
const ReactQueryDevtools = import.meta.env.DEV
    ? lazy(() =>
        import('@tanstack/react-query-devtools').then((mod) => ({
            default: mod.ReactQueryDevtools
        }))
    )
    : null;

// Foundation for Phase 4 (TanStack Query). Defaults per the modernization
// plan: a 60s staleTime avoids refetch storms on nav between cached screens,
// retry: 1 keeps failures visible quickly instead of masking them behind
// TanStack's default 3 retries, and refetchOnWindowFocus is off since this
// app has no realtime data that needs it. Deliberately no custom `retry`
// *policy* (e.g. skipping retries on 401/403) -- SuperTokens' own patched
// `fetch` already retries once internally on a session refresh, and a
// competing retry policy here would fight that.
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 60_000,
            retry: 1,
            refetchOnWindowFocus: false
        }
    }
});

SuperTokens.init({
    appInfo: {
        appName: 'Ultiverse',
        apiDomain: APP_CONFIG.apiUrl,
        websiteDomain: APP_CONFIG.url,
        apiBasePath: '/v1/auth',
        apiGatewayPath: APP_CONFIG.apiGatewayPath,
        websiteBasePath: '/auth'
    },

    getRedirectionURL: (context) => {
        if (context.action === 'TO_AUTH') {
            // Move handling the SIGN_IN_AND_UP action here
            return '/auth';
        }
    },
    // Disabled to work around a bug in supertokens-auth-react@0.30.2's
    // WithShadowDom component: its mount effect called attachShadow()
    // with no cleanup/idempotency guard, so React's StrictMode double-
    // invoke (React 18+) threw "Unable to re-attach to existing
    // ShadowDOM" and the auth UI had to be recovered by an error
    // boundary. useShadowDom/style moved from per-recipe config to this
    // root SuperTokens.init() config as of supertokens-auth-react@0.42.0.
    // The `[data-supertokens~=...]` style overrides below work identically
    // without shadow DOM.
    useShadowDom: false,
    // Repointed from interpolated lightModePalette.primary.main literals to the
    // CSS custom properties in src/index.css (Phase 6). Since useShadowDom is
    // off (see above), this is genuinely global CSS -- `.dark` on <html> reaches
    // it, so these variables now flip with the rest of the app's dark mode
    // instead of the auth UI staying hardcoded light in every theme.
    style: `
        [data-supertokens~=container] {
            --palette-background: 255, 255, 255;
            --palette-inputBackground: 250, 250, 250;
            --palette-inputBorder: var(--brand-primary-rgb);
            --palette-textPrimary: 0, 0, 0;
            --palette-textInput: 34, 34, 34;
            --palette-textLink: var(--brand-primary-rgb);
            --palette-primary: var(--brand-primary-rgb);
        }
        .dark [data-supertokens~=container] {
            --palette-background: 23, 23, 23;
            --palette-inputBackground: 38, 38, 38;
            --palette-textPrimary: 245, 245, 245;
            --palette-textTitle: 245, 245, 245;
            --palette-textLabel: 245, 245, 245;
            --palette-textInput: 245, 245, 245;
            --palette-textGray: 163, 163, 163;
            --palette-secondaryText: 163, 163, 163;
            --palette-superTokensBrandingBackground: 38, 38, 38;
            --palette-superTokensBrandingText: 115, 115, 115;
        }
        [data-supertokens~=button] {
            background-color: var(--brand-primary);
            border-color: var(--brand-primary);
            color: white;
        }
        [data-supertokens~=inputWrapper]:focus-within {
            border-color: var(--brand-primary);
            box-shadow: color-mix(in srgb, var(--brand-primary) 25%, transparent) 0px 0px 0px 0.2rem;
        }
        [data-supertokens~=link] {
            color: var(--brand-primary);
        }
        [data-supertokens~=inputAdornment] {
            --palette-primary: var(--brand-primary-rgb);
        }
    `,
    recipeList: [EmailPassword.init(), Session.init()]
});

function App() {
    return (
        <SuperTokensWrapper>
            <QueryClientProvider client={queryClient}>
                <ConfirmProvider>
                    <Main />
                </ConfirmProvider>
                {ReactQueryDevtools && (
                    <Suspense fallback={null}>
                        <ReactQueryDevtools initialIsOpen={false} />
                    </Suspense>
                )}
            </QueryClientProvider>
        </SuperTokensWrapper>
    );
}

export default App;
